import * as React from 'react';
import { useAmplitudeTrack, toSnakeCase } from './scripts/amplitude_utils';
import {
    AppBar,
    Avatar,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Tooltip,
    Typography,
    useTheme,
    Box,
    Stack
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home';
import DomainIcon from '@mui/icons-material/Domain';
import MenuIcon from '@mui/icons-material/Menu';
import BugReportIcon from '@mui/icons-material/BugReport';
import PeopleIcon from '@mui/icons-material/People';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import AddLinkIcon from '@mui/icons-material/AddLink';
import AdbIcon from '@mui/icons-material/Adb';
import {PermIdentityOutlined} from "@mui/icons-material";
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import DescriptionIcon from '@mui/icons-material/Description';

import {
    BrowserRouter as Router,
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useNavigate,
    useParams
} from 'react-router-dom';

import LoginPage from './pages/login/login';
import AdminPage from './pages/admin/admin';
import LandingPage from './pages/landing_page/landing_page';
import TermsAndConditions from './pages/terms_and_conditions/terms_and_conditions';

import {SnackbarProvider} from 'notistack';
import Snackbar from "./scripts/snackbar";

import Popover from '@mui/material/Popover';
import PopupState, {bindPopover, bindTrigger} from 'material-ui-popup-state';
import SitesOverview from './pages/elion_site/sites_overview';

import SitePage from './pages/elion_site/site_page';

import {SchematicProvider} from './context/schematic_provider';
import {BackendProvider} from './context/backend_provider';
import {LiveDataProvider} from './context/live_data_provider';

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import BoxAlerts from './components/alerting/box_alerts';
import useBackend, {useAllowedComponents, useAllowedFeatures, useLogin, useLoginStatus} from './hooks/use_backend';
import ProfilePage from "./pages/profile/profile";
import PageDoesNotExists from "./pages/error_pages/not_found";
import AccessDeniedPage from "./pages/error_pages/access_denied";
import Privilege from "./enums/privilege_enum";
import OverviewCrmTab from "./pages/crm/crm_overview_tab";
import {REACT_QUERY_KEYS} from "./enums/react_query_key_enum";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {fetchSiteData} from "./scripts/api_v2/site_client";
import GeneralTab from "./pages/elion_site/tabs/general_tab";
import DevicesTab from "./pages/elion_site/tabs/devices_tab";
import MeteringTab from "./pages/elion_site/tabs/metering_tab";
import FinancialTab from "./pages/elion_site/tabs/financial_tab";
import SchematicTab from "./pages/elion_site/tabs/schematic_tab";
import LogTab from "./pages/elion_site/tabs/log_tab";
import {NAVIGATION_PATH} from "./enums/navigation_enum";
import UserManagementTable from "./pages/user_management/user_management";
import {ModalProvider} from './context/modal_provider';
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import StatisticsTab from './pages/elion_site/tabs/statistics_tab';
import {ElindusRedColors, shortenUsername} from './scripts/utils';
import CommissioningTab from "./pages/elion_site/tabs/commissioning_tab/commissioning_tab";
import InstallationTab from "./pages/elion_site/tabs/installation_tab";
import AlertsTab from "./pages/elion_site/tabs/alerts_tab";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import SchoolIcon from '@mui/icons-material/School';
import UserForm from './components/form/user_form';
import {UserActions} from "./pages/user_management/user_actions_enum";
import {fetchElionBoxStatus} from "./scripts/api_v2/status_client"
import ElionLogo from 'images/Elion_Logo_Wordmark_RGB_Red.svg';
import WarningIcon from '@mui/icons-material/Warning';
import Privileges from "./enums/privilege_enum";

import {CommissioningStateProvider} from "./pages/elion_site/tabs/commissioning_tab/components/hooks/use_commissioning_state_changes";
import {AlertColors} from "./components/alerting/alert_enums";

import config from './scripts/config';

// used for side/avatar menu
let routeDict = {
    [NAVIGATION_PATH.HOME]: {name: "Home", icon: <HomeIcon/>, order: 1},
    [NAVIGATION_PATH.SITE_ROOT]: {name: "Sites", icon: <DomainIcon/>, order: 2},
    [NAVIGATION_PATH.CRM_MANAGEMENT]: {name: "Site-contract management", icon: <AddLinkIcon/>, order: 3},
    [NAVIGATION_PATH.ADMIN]: {name: "Admin", icon: <AdbIcon/>, order: 4},
    [NAVIGATION_PATH.USERS]: {name: "Users", icon: <PeopleIcon/>, order: 5},
    [NAVIGATION_PATH.PROFILE]: {name: "Profile", icon: <PermIdentityOutlined/>, order: 6},
    [NAVIGATION_PATH.TERMS_AND_CONDITIONS]: {name: "Terms & Conditions", icon: <DescriptionIcon/>, order: 7},
    //[NAVIGATION_PATH.FAQ]: {name: "FAQ", icon: <LiveHelpIcon/>, order: 7},
}

// used for guarding which options we show in the side menu
let sideMenuRequiredFeaturesRoutesDict = {
    [Privilege.ADMIN_OVERVIEW.feature]: [NAVIGATION_PATH.ADMIN],
    [Privilege.UM_MANAGE_USERS.feature]: [NAVIGATION_PATH.USERS],
    [Privilege.DEVICES_OVERVIEW.feature]: [NAVIGATION_PATH.SITE_ROOT, NAVIGATION_PATH.HOME],
    [Privilege.CRM_CRM_OVERVIEW.feature]: [NAVIGATION_PATH.CRM_MANAGEMENT],
}

// used for guarding which options we show in the avatar menu
let avatarMenuRequiredFeaturesRoutesDict = {
    [Privilege.PROFILE_OVERVIEW.feature]: [NAVIGATION_PATH.PROFILE],
}


// generate return icon which navigates back to the base path
const generateReturnIconButton = (location, navigate, setOpen, open, trackButtonClicked) => {
    let path_parts = location.pathname.split('/')
    if (path_parts.length > 2 && !location.pathname.includes(NAVIGATION_PATH.CRM_MANAGEMENT)) {
        const base_path = '/' + path_parts[1]
        return (
            <IconButton
                color="inherit"
                aria-label="go back"
                edge="start"
                sx={{marginRight: 1}}
                onClick={() => {
                    trackButtonClicked('header_back', 'header', { from_path: location.pathname, to_path: base_path });
                    navigate(base_path);
                }}
            >
                <ArrowBackIosNewIcon/>
            </IconButton>
        );
    } else {
        return (
            <IconButton
                color="inherit"
                aria-label="open drawer"
                onClick={() => {
                    trackButtonClicked(open ? 'drawer_close' : 'drawer_open', 'header');
                    setOpen(!open);
                }}
                edge="start"
                sx={{marginRight: 1}}
            >
                <MenuIcon/>
            </IconButton>
        );
    }
};

function UserAvatar() {
    const navigate = useNavigate();
    const theme = useTheme();
    const backend = useBackend();
    const allowedFeatures = useAllowedFeatures();
    const queryClient = useQueryClient()
    const login = useLogin();
    const { trackButtonClicked } = useAmplitudeTrack();

    const onLogout = async () => {
        trackButtonClicked('logout', 'avatar_menu');
        const status = await backend.tryLogout();
        queryClient.clear()
        if (status === true) {
            navigate(NAVIGATION_PATH.LOGIN_PATH);
        }
    }

    return (
        <PopupState variant="popover" popupId="demo-popup-popover">
            {(popupState) => (
                <div>
                    <IconButton {...bindTrigger(popupState)} onClick={(event) => {
                        trackButtonClicked('avatar_menu_open', 'header');
                        bindTrigger(popupState).onClick(event);
                    }}>
                        <Avatar sx={{bgcolor: ElindusRedColors(login.name)}}>{shortenUsername(login.name)}</Avatar>
                    </IconButton>
                    <Popover
                        {...bindPopover(popupState)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                        }}
                    >
                        <List sx={{width: 200}}>
                            {(() => {
                                // generates the guarded avatar menu routes
                                return getMenuRoutes(allowedFeatures, avatarMenuRequiredFeaturesRoutesDict).map((route, index) => (
                                    <ListItem disablePadding key={route}>
                                        <ListItemButton onClick={() => {
                                            trackButtonClicked(`avatar_menu_${toSnakeCase(routeDict[route].name)}`, 'avatar_menu', { to_path: route });
                                            navigate(route);
                                        }}>
                                            <ListItemIcon sx={{
                                                mr: -2,
                                                color: theme.palette.primary.main
                                            }}>{routeDict[route]["icon"]}</ListItemIcon>
                                            <ListItemText primary={routeDict[route]["name"]}
                                                          sx={{color: theme.palette.primary.main}}/>
                                        </ListItemButton>
                                    </ListItem>
                                ));
                            })()}
                            <ListItem disablePadding>
                                <ListItemButton onClick={onLogout}>
                                    <ListItemIcon sx={{
                                        mr: -2,
                                        color: theme.palette.primary.main
                                    }}><PowerSettingsNewIcon/></ListItemIcon>
                                    <ListItemText primary={"Log out"} sx={{color: theme.palette.primary.main}}/>
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Popover>
                </div>
            )}
        </PopupState>
    );
}

function SiteTitle() {
    const params = useParams();
    const backend = useBackend();

    const {data: siteData} = useQuery({
        queryKey: [REACT_QUERY_KEYS.SITE_DATA, backend.fetch, params.site_id],
        queryFn: () => fetchSiteData(backend.fetch, params.site_id),
        enabled: false // sitePage will refresh the query
    });

    const theme = useTheme();

    const titleElements = siteData ? [
        <b key={0} style={{color: theme.palette.primary.main}}>{siteData.site_name}</b>,
    ] : [];

    if (siteData?.installer_status) {
        let iconValue = null;
        let alertInfo = null;

        if (siteData.is_debug === true) {
            iconValue = 6; // BugIcon
            alertInfo = "Debug";
        } else {
            const status = siteData.admin_status;

            if (!status || status === "healthy") {
                iconValue = 7; // value not in boxAlert -> default check icon
                alertInfo = "Healthy";
            } else if (status === "warning") {
                iconValue = 1; // HourglassEmptyIcon
                alertInfo = "Warning";
            } else if (status === "error") {
                iconValue = 2; // WarningIcon
                alertInfo = "Error";
            } else if (status === "config") {
                iconValue = 3; // InfoIcon
                alertInfo = "Config";
            } else if (status === "critical") {
                iconValue = 4; // ErrorIcon
                alertInfo = "Critical";
            } else if (status === "pending") {
                iconValue = 5; // HourglassBottomIcon
                alertInfo = "Pending";
            }

            if (status && status !== "healthy" && siteData.alerts && Array.isArray(siteData.alerts) && siteData.alerts.length > 0) {
                const alertInfos = siteData.alerts
                    .map(a => a.info)
                    .filter(info => info);
                if (alertInfos.length > 0) {
                    alertInfo = alertInfos.join("\n");
                }
            }
        }

        const alerts = iconValue !== undefined && alertInfo ? [{ icon: iconValue, info: alertInfo }] : alertInfo ? [{ icon: undefined, info: alertInfo }] : [];
        titleElements.push(
            <BoxAlerts key={2} sx={{marginLeft: 1}} hideOK alerts={alerts}/>
        );
    }

    return siteData ? titleElements : null
}


function getMenuRoutes(allowedFeatures, menuRequiredFeaturesForRoutesDict) {
    const uniquePaths = new Set();

    allowedFeatures.forEach(feature => {
        if (menuRequiredFeaturesForRoutesDict[feature]) {
            menuRequiredFeaturesForRoutesDict[feature].forEach(path => {
                uniquePaths.add(path);
            });
        }
    });

    return Array.from(uniquePaths)
}


// /contract-management/new-installation is the only nested route for which we want to use name as title currently
// should loop calling popPathSegment if we nest routes even more
function popPathSegment(pathname) {
    let lastSlashIndex = pathname.lastIndexOf('/');
    return pathname.slice(0, lastSlashIndex);
}

function checkRouteDictForRootIconEntry(pathname) {
    let rootPath = pathname
    if (!routeDict[pathname]) {
        rootPath = popPathSegment(pathname)
    }
    return routeDict[rootPath] ? routeDict[rootPath]["icon"] : null
}

function checkRouteDictForRootNameEntry(pathname) {
    let rootPath = pathname
    if (!routeDict[pathname]) {
        rootPath = popPathSegment(pathname)
    }
    return routeDict[rootPath] ? routeDict[rootPath].name.toUpperCase() : null
}

const AcademyButton = () => {
    const { trackButtonClicked } = useAmplitudeTrack();
    return <Tooltip title="Go to academy">
        <IconButton sx={{marginRight: 1}} size='large' color='primary' onClick={() => {
            trackButtonClicked('academy', 'header');
            window.open('https://elionpartner.rakoo.com/login', '_blank');
        }}>
            <SchoolIcon/>
        </IconButton>
    </Tooltip>

}

function copyToClipboard(setShowNumber, isShowNumber, text) {
    setShowNumber(isShowNumber)
    if (isShowNumber) {
        navigator.clipboard.writeText(text).then(() => {
            Snackbar.successShort("Copied to clipboard");
        }).catch(err => {console.error("Failed to copy: ", err);});
    }
}

const ContactSupport = ({showNumber, setShowNumber}) => {
    const { trackButtonClicked } = useAmplitudeTrack();
    const contactSupportNumber = '+32 56 94 66 88';
    return <Tooltip title="Contact customer support">
            <Stack sx={{ marginRight: 2 }} display='flex' alignItems='center' direction='vertical' onClick={() => {
                trackButtonClicked('contact_support', 'header');
                copyToClipboard(setShowNumber, true, contactSupportNumber);
            }}>
            <IconButton
  
                color="primary"
            >
                <HeadsetMicIcon />
            </IconButton>
            <Typography variant="body2"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => copyToClipboard(setShowNumber, false, contactSupportNumber)}
            >
                {contactSupportNumber}
            </Typography>
            </Stack>
        </Tooltip>
}

// const AcademyButton = () => {
//     return <Tooltip title="Go to academy">
//         <IconButton sx={{marginRight: 1}} size='large' color='primary' onClick={() => {
//             window.open('https://elionpartner.rakoo.com/login', '_blank');
//         }}>
//             <SchoolIcon/>
//         </IconButton>
//     </Tooltip>

// }

function ElionBoxStatusElement({ status }) {
    const allowedFeatures = useAllowedFeatures();
    const { trackButtonClicked } = useAmplitudeTrack();
    const color = (() => {
        switch (status?.STATUS) {
            case 0: return AlertColors.SUCCESS;
            case 1: return AlertColors.WARNING;
            case 2: return AlertColors.ERROR;
            default: return AlertColors.INFO;
        }
    })();

    const icon = (() => {
        switch (status?.STATUS) {
            case 1: return <WarningIcon htmlColor='white'/>;
            case 2: return <WarningIcon htmlColor='white'/>;
            default: return '';
        }
    })();

    if (status === undefined || status === null || status?.STATUS <= 0 || (status?.STATUS === 1 && !(allowedFeatures.includes(Privileges.ADMIN_OVERVIEW.feature)))){
        return ('')
    }
    return (
        <Box>
            <Box height="35px" display="flex" alignItems="center" justifyContent="space-between" sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: color,
                    px: "30%"
                }}>
                {icon}
                <Box display="flex" alignItems="center" sx={{ ml:1}}>
                    <Typography noWrap color="white">{ status?.STATUS === 2 ? status?.PLATFORM_INFO : status?.INFO} </Typography>
                    <IconButton onClick={() => {
                        trackButtonClicked('platform_status', 'status_banner');
                        window.open(config.REACT_APP_STATUS_URL);
                    }}>
                    <OpenInNewIcon sx={{fontSize: 'medium', color:'white'}}/>
                    </IconButton>
                </Box>
                {icon}
            </Box>
        </Box>
    )
}

// Readable page name for journeys: site detail pages share one name, the tab is tracked separately
function getPageName(pathname) {
    const segments = pathname.split('/');
    if (segments[1] === 'sites' && segments.length > 2) return 'site_detail';
    return routeDict[pathname] ? toSnakeCase(routeDict[pathname].name) : toSnakeCase(pathname) || 'unknown';
}

function Layout({drawer}) {
    const location = useLocation();
    const previousPathRef = React.useRef(location.pathname);

    const { trackEvent, trackButtonClicked } = useAmplitudeTrack();

    React.useEffect(() => {
        trackEvent('page_viewed', {
            page_name: getPageName(location.pathname),
            page_path: location.pathname,
            previous_page_name: getPageName(previousPathRef.current),
            previous_path: previousPathRef.current,
        });
        previousPathRef.current = location.pathname;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const [open, setOpen] = React.useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const allowedFeatures = useAllowedFeatures()
    const [showNumber, setShowNumber] = React.useState(false);

    const {data: elionBoxStatus} = useQuery({
        queryKey: [REACT_QUERY_KEYS.ELION_STATUS],
        queryFn: () => fetchElionBoxStatus(),
        enabled: true,
        refetchInterval: 300000
    });

    return <React.Fragment><AppBar
        position="sticky"
        elevation={0}
        sx={{
            bgcolor: "white",
            color: (theme) => theme.palette.primary.main,
            borderBottom: (theme) => "1px solid " + theme.palette.background.border
        }}
    >
        <Toolbar sx={{flexWrap: 'wrap'}}>
            {generateReturnIconButton(location, navigate, setOpen, open, trackButtonClicked)}
            {checkRouteDictForRootIconEntry(location.pathname)}
            <Typography variant="h6" color="inherit" noWrap
                        sx={{flexGrow: 1, fontWeight: "700", pl: 1, display: "flex", alignItems: "center"}}>
                {(location.pathname.includes("/sites/")
                    ? <SiteTitle/> :
                    checkRouteDictForRootNameEntry(location.pathname))}
            </Typography>
            <ContactSupport showNumber={showNumber} setShowNumber={setShowNumber}/>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                }}
                >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <img src={ElionLogo} alt="Elion Logo" style={{ height: 70 }} />
                </Box>
            </Box>
            <AcademyButton/>
            <UserAvatar/>
        </Toolbar>
    </AppBar>
        <Drawer
            sx={{
                width: 200,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: 200,
                    boxSizing: 'border-box',
                },
                display: 'flex',
                flexDirection: 'column'
            }}
            position="persistent"
            anchor="left"
            open={open}
        >
            <div style={{display: "flex", justifyContent: "flex-end"}}>
                <IconButton onClick={() => {
                    trackButtonClicked('drawer_close', 'drawer');
                    setOpen(false);
                }}>
                    <ChevronLeftIcon color={theme.palette.secondary.main}/>
                </IconButton>
            </div>
            <Divider/>
            <List sx={{flexGrow: 1}}>
                {(() => {
                    return getMenuRoutes(allowedFeatures, sideMenuRequiredFeaturesRoutesDict)
                        .sort((a, b) => routeDict[a].order - routeDict[b].order)
                        .map((route, index) => (
                            <React.Fragment key={route}>
                                <ListItem disablePadding>
                                    <ListItemButton onClick={() => {
                                        trackButtonClicked(`drawer_${toSnakeCase(routeDict[route].name)}`, 'drawer', { to_path: route });
                                        navigate(route);
                                        setOpen(false)
                                    }}>
                                        <ListItemIcon sx={{
                                            mr: -2,
                                            color: location.pathname === route ? theme.palette.primary.main : theme.palette.text.primary_bold
                                        }}>{routeDict[route]["icon"]}</ListItemIcon>
                                        <ListItemText primary={routeDict[route]["name"]}
                                                      sx={{color: location.pathname === route ? theme.palette.primary.main : theme.palette.text.primary_bold}}/>
                                    </ListItemButton>
                                </ListItem>
                            </React.Fragment>
                        ));
                })()}
                <Divider/>
            </List>
            <List sx={{mt: 'auto'}}>
                <ListItem disablePadding>
                    <ListItemButton onClick={() => {
                        trackButtonClicked('drawer_terms_and_conditions', 'drawer', { to_path: NAVIGATION_PATH.TERMS_AND_CONDITIONS });
                        navigate(NAVIGATION_PATH.TERMS_AND_CONDITIONS);
                        setOpen(false)
                    }}>
                        <ListItemIcon sx={{
                            mr: -2,
                            color: location.pathname === NAVIGATION_PATH.TERMS_AND_CONDITIONS ? theme.palette.primary.main : theme.palette.text.primary_bold
                        }}><DescriptionIcon/></ListItemIcon>
                        <ListItemText
                            primary="Terms & Conditions"
                            sx={{color: location.pathname === NAVIGATION_PATH.TERMS_AND_CONDITIONS ? theme.palette.primary.main : theme.palette.text.primary_bold}}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
            <div style={{marginTop: 'auto', justifyContent: 'center', display: 'flex'}}>
                <Typography sx={{color: 'rgba(47,79,79,0.5)'}} display="block">Technical support?</Typography>
            </div>
            <div style={{marginTop: 'auto', justifyContent: 'center', display: 'flex', marginBottom: 5}}>
                <Typography sx={{color: 'rgba(47,79,79,0.5)'}} display="block">+32 56 94 66 88</Typography>
            </div>
            <div style={{marginTop: 'auto', justifyContent: 'center', display: 'flex'}}>
                <Typography sx={{color: 'rgba(47,79,79,0.5)'}} variant="overline" display="block">version 3.4.0</Typography>
            </div>
        </Drawer>
        <ElionBoxStatusElement status={elionBoxStatus}/>
        <Outlet/>
    </React.Fragment>
}

export default function Navigation() {
    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <BackendProvider>
                <SnackbarProvider maxSnack={3} autoHideDuration={3000}
                                  anchorOrigin={{vertical: 'top', horizontal: 'right'}}>
                    <LiveDataProvider>
                        <ModalProvider>
                            <Router>
                                <CustomRoutes/>
                            </Router>
                        </ModalProvider>
                    </LiveDataProvider>
                </SnackbarProvider>
            </BackendProvider>
        </LocalizationProvider>
    );
}

function CustomRoutes() {
    return <Routes>
        <Route path="/" element={<Layout drawer/>}>
            <Route index element={<GuardedElement element={LandingPage}
                                                  required_privilege={Privilege.DEVICES_OVERVIEW}/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.SITE_ROOT} element={<Layout drawer/>}>
            <Route index element={<GuardedElement element={SitesOverview}
                                                  required_privilege={Privilege.DEVICES_OVERVIEW}/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.TERMS_AND_CONDITIONS} element={<Layout drawer/>}>
            <Route index element={<GuardedElement element={TermsAndConditions}/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.ADMIN} element={<Layout drawer/>}>
            <Route index element={<GuardedElement element={AdminPage}
                                                  required_privilege={Privilege.ADMIN_OVERVIEW}/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.USERS} element={<Layout drawer/>}>
            <Route index
                   element={<GuardedElement element={UserManagementTable}
                                            required_privilege={Privilege.UM_MANAGE_USERS}
                   />}/>
        </Route>
        <Route path={NAVIGATION_PATH.CRM_MANAGEMENT} element={<Layout drawer/>}>
            <Route index element={<GuardedElement element={OverviewCrmTab}
                                                  required_privilege={Privilege.CRM_CRM_OVERVIEW}/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.SITE_ROOT} element={<Layout drawer/>}>
            <Route path=":site_id" element={<SitePage/>}>
                <Route index element={<GuardedElement element={GeneralTab}
                                                      required_privilege={Privilege.DEVICES_OVERVIEW}/>}/>
                <Route path={NAVIGATION_PATH.GENERAL}
                       element={<GuardedElement element={GeneralTab}
                                                required_privilege={Privilege.DEVICES_GENERAL}/>}/>
                <Route path={NAVIGATION_PATH.DEVICES}
                       element={<GuardedElement element={DevicesTab}
                                                required_privilege={Privilege.DEVICES_DEVICES}/>}/>
                <Route path={NAVIGATION_PATH.INSTALLATION} element={<SchematicProvider>
                    <GuardedElement element={InstallationTab}
                                    required_privilege={Privilege.DEVICES_SCHEMATIC}/>
                </SchematicProvider>}/>
                <Route path={NAVIGATION_PATH.SCHEMATIC} element={<SchematicProvider>
                    <GuardedElement element={SchematicTab}
                                    required_privilege={Privilege.DEVICES_SCHEMATIC}/>
                </SchematicProvider>}/>
                <Route path={NAVIGATION_PATH.METERING}
                       element={<GuardedElement element={MeteringTab}
                                                required_privilege={Privilege.DEVICES_METERING}/>}/>
                <Route path={NAVIGATION_PATH.LOG} element={<GuardedElement element={LogTab}
                                                                           required_privilege={Privilege.DEVICES_LOG}/>}/>
                  <Route path={NAVIGATION_PATH.COMMISSIONING}
                       element={
                           <CommissioningStateProvider>
                               <GuardedElement element={CommissioningTab}
                                               required_privilege={Privilege.CP_OVERVIEW}/>
                           </CommissioningStateProvider>
                       }/>
                  <Route path={NAVIGATION_PATH.ALERTS}
                      element={<GuardedElement element={AlertsTab}
                                      required_privilege={Privilege.DEVICES_OVERVIEW}/>}/>
                  <Route path={NAVIGATION_PATH.STATISTICS}
                       element={<GuardedElement element={StatisticsTab}
                                                required_privilege={Privilege.DEVICES_OVERVIEW}/>}/>
                  <Route path={NAVIGATION_PATH.FINANCIAL}
                      element={<GuardedElement element={FinancialTab}
                                      required_privilege={Privilege.DEVICES_OVERVIEW}/>}/>
            </Route>
        </Route>
        <Route path={NAVIGATION_PATH.LOGIN_PATH}>
            <Route index element={<LoginPage/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.PROFILE} element={<Layout drawer/>}>
            <Route index element={<GuardedElement element={ProfilePage}
                                                  required_privilege={Privilege.PROFILE_OVERVIEW}/>}/>
        </Route>
        <Route path={NAVIGATION_PATH.PASSWORD_RESET}>
            <Route path=":uuid" element={<UserForm actionType={UserActions.RESET_PASSWORD_TEMP_LINK}/>}>
            </Route>
        </Route>
        <Route path={NAVIGATION_PATH.REQUEST_PASSWORD_RESET}>
            <Route index element={
                <UserForm actionType={UserActions.REQUEST_RESET_TEMP_LINK}/>
            }/>
        </Route>
        <Route path={NAVIGATION_PATH.WILDCARD} element={<Layout drawer={true}/>}>
            <Route
                path={NAVIGATION_PATH.WILDCARD}
                element={<PageDoesNotExists/>}
            />
        </Route>
        <Route path={NAVIGATION_PATH.DENIED} element={<Layout drawer/>}>
            <Route index element={<AccessDeniedPage/>}/>
        </Route>
    </Routes>
}

const GuardedElement = ({element: Element, required_privilege = null}) => {
    const allowedFeatures = useAllowedFeatures()
    const allowedComponents = useAllowedComponents()
    const login = useLogin()
    const loginStatus = useLoginStatus();
    const queryClient = useQueryClient();
    const backend = useBackend();

    if ((loginStatus <= 0 || !login)) {
        return <Navigate to={NAVIGATION_PATH.LOGIN_PATH} replace/>
    }

    // Check if T&C acceptance is required
    if (backend.termsRequired) {
        // Redirect to login page which will show the T&C dialog
        // The login state is preserved, so the dialog will appear
        return <Navigate to={NAVIGATION_PATH.LOGIN_PATH} replace/>
    }

    // Check feature level first, then check for specific components
    if (required_privilege === null || allowedFeatures.includes(required_privilege.feature)) {
        if (required_privilege === null || required_privilege.component === null || allowedComponents.includes(required_privilege.getPrivilegeKey())) {
            return <Element required_privilege={required_privilege}/>;
        }
    }
    // default
    Snackbar.error("Access denied");
    queryClient.clear()
    return <Navigate to={NAVIGATION_PATH.LOGIN_PATH} replace/>
}
